// Loads the real `monaco-editor` package once (never on app load - see this
// module's own callers, both of which only mount an editor once a console
// surface is actually opened) and wires its Web Worker so the editor's core
// features (bracket matching, find/replace, etc.) work without ever hitting
// a CDN - the whole point of vendoring `monaco-editor` as a real dependency
// instead of Monaco's own CDN loader path (matching this repo's own origin-
// isolation posture: `/play/<slug>` already avoids third-party origins for
// anything the play surface depends on).
//
// The worker import uses `monaco-editor`'s own tree-shakeable subpath
// (`monaco-editor/editor/editor.worker.js`, new as of monaco-editor 0.56.0 -
// see that version's own CHANGELOG "Reorganizes the exported ESM modules"
// entry) rather than the older `monaco-editor/esm/vs/editor/editor.worker`
// path most examples online still show: the older path does not resolve
// under 0.56.0's actual `exports` map (verified directly - it 404s/fails
// resolution, doubling the `esm/vs` segment), so it is not a style choice.
//
// This app does not use `@monaco-editor/react`: as of writing, that
// package's own bundled type declarations (`@monaco-editor/loader`'s
// `lib/types.d.ts`) import `monaco-editor/esm/vs/editor/editor.api` - the
// same now-broken pre-0.56.0 deep path - so pairing it with a current
// `monaco-editor` fails `tsc` outright (confirmed directly: reverting to
// the last `monaco-editor` version that still exposes an unrestricted `"./*":
// "./*"` export wildcard does not fix it either, since Node's exports
// resolution requires an exact extension match through that wildcard and
// the package's own `.d.ts` omits one). Mounting `monaco-editor` directly
// (`HollowScriptEditor.tsx`) is the actually-current, well-documented
// integration path and sidesteps a wrapper dependency's release cadence
// entirely.

import type * as Monaco from 'monaco-editor';
import EditorWorker from 'monaco-editor/editor/editor.worker.js?worker';

let configured = false;

/** Idempotent: `MonacoEnvironment` is a page-global, set once no matter how
 * many editor instances mount/unmount over the page's lifetime. */
export function ensureMonacoWorkerConfigured(): void {
  if (configured) return;
  configured = true;
  self.MonacoEnvironment = {
    getWorker: () => new EditorWorker(),
  } satisfies Monaco.Environment;
}
